import React from 'react';
import { Box, Heading, Tabs } from '@wix/design-system';
import { horizontalNavBarDataHooks } from './data-hooks';
import styles from './horizontal-nav-bar.module.scss';

/** A nav bar item, in the shape the WDS Tabs renders - arrives render-ready
 *  from the producing service; this component only feeds it through. */
export interface HorizontalNavBarItem {
  id: string | number;
  title: React.ReactNode;
}

interface HorizontalNavBarBaseProps {
  items: HorizontalNavBarItem[];
  selectedItemKey?: string;
  onItemClick(item: HorizontalNavBarItem, index: number): void;
}

interface PillsNavBarProps extends HorizontalNavBarBaseProps {
  skin?: 'pills';
  sideContent?: React.ReactNode;
}

interface TabsNavBarProps extends HorizontalNavBarBaseProps {
  skin: 'tabs';
  title: string;
}

export type HorizontalNavBarProps = PillsNavBarProps | TabsNavBarProps;

export function HorizontalNavBar(props: HorizontalNavBarProps) {
  if (props.skin === 'tabs') {
    return <TabsNavBar {...props} />;
  }
  return <PillsNavBar {...props} />;
}

function TabsNavBar({
  items,
  selectedItemKey,
  onItemClick,
  title,
}: TabsNavBarProps) {
  return (
    <Box width="100%" gap="24px" verticalAlign="middle">
      <Heading size="extraLarge" dataHook={horizontalNavBarDataHooks.title}>
        {title}
      </Heading>
      <Tabs
        items={items}
        activeId={selectedItemKey}
        onClick={(item) => onItemClick(item, items.indexOf(item))}
        size="medium"
        showDivider={false}
        horizontalPadding={false}
      />
    </Box>
  );
}

function PillsNavBar({
  items,
  selectedItemKey,
  onItemClick,
  sideContent,
}: PillsNavBarProps) {
  return (
    <Tabs
      className={styles.pills}
      dataHook={horizontalNavBarDataHooks.root}
      items={items}
      activeId={selectedItemKey}
      onClick={(item) => onItemClick(item, items.indexOf(item))}
      size="small"
      showDivider={false}
      horizontalPadding={false}
      scrollOnOverflow
      sideContent={
        sideContent ? (
          <Box dataHook={horizontalNavBarDataHooks.sideContent}>
            {sideContent}
          </Box>
        ) : undefined
      }
    />
  );
}
